// Web NFC writing helper — Android Chrome / Edge only.
// Spec: https://developer.mozilla.org/en-US/docs/Web/API/Web_NFC_API
//
// The Web NFC API is only available on Android in Chromium-based browsers and
// requires HTTPS, foreground tab, and a user gesture for the first call. The
// browser shows a permission prompt the first time `NDEFReader.write()` is
// invoked from a given origin; if the user dismisses or denies that prompt,
// subsequent writes immediately throw `NotAllowedError`, which is why the
// Android OS dispatcher (the "Complete action with Tags / WeChat / New tag
// scan" chooser) appears instead of the URL being programmed.

export function isNfcSupported() {
  return typeof window !== "undefined" && "NDEFReader" in window;
}

/**
 * Best-effort check of the current NFC permission state without triggering a
 * prompt. Returns one of "granted" | "prompt" | "denied" | "unsupported".
 */
export async function getNfcPermissionState() {
  try {
    if (typeof navigator === "undefined" || !navigator.permissions) return "unsupported";
    // The Permissions API spec exposes "nfc" as a valid name in Chromium.
    const status = await navigator.permissions.query({ name: "nfc" });
    return status.state || "prompt";
  } catch (_e) {
    // Some browsers throw on unknown names — treat as prompt so we still try.
    return "prompt";
  }
}

/**
 * Browser/UA hint — useful so we can warn users that Samsung Internet / Brave /
 * Firefox / in-app browsers don't actually support Web NFC even on Android.
 */
export function describeBrowserSupport() {
  if (typeof navigator === "undefined") return { ok: false, reason: "No navigator." };
  const ua = navigator.userAgent || "";
  const isAndroid = /Android/i.test(ua);
  if (!isAndroid) {
    return {
      ok: false,
      reason: "Web NFC writing only works on Android phones. Open this page on an Android device.",
    };
  }
  if (!isNfcSupported()) {
    return {
      ok: false,
      reason:
        "This browser does not support Web NFC. Use Google Chrome or Microsoft Edge on Android (Samsung Internet, Firefox and in-app browsers like WhatsApp will not work).",
    };
  }
  if (typeof window !== "undefined" && window.location && window.location.protocol !== "https:") {
    return {
      ok: false,
      reason: "Web NFC requires an https:// URL. Open this page over HTTPS.",
    };
  }
  return { ok: true };
}

/**
 * Write a single URL record to the next NFC tag the user taps against the
 * device. Resolves when the write succeeds; rejects with a human-readable
 * Error otherwise.
 *
 * @param {string} url - Absolute https URL to encode on the tag.
 * @param {object} [opts]
 * @param {AbortSignal} [opts.signal] - Optional AbortSignal to cancel the
 *   pending write (e.g. user closed the dialog).
 * @returns {Promise<void>}
 */
export async function writeNfcUrl(url, opts = {}) {
  const support = describeBrowserSupport();
  if (!support.ok) {
    throw new Error(support.reason);
  }
  if (!url || typeof url !== "string") {
    throw new Error("No URL was supplied to write to the tag.");
  }
  if (!/^https?:\/\//i.test(url)) {
    throw new Error("The tag URL must be an absolute https:// link.");
  }

  // Detect a previously-denied permission before calling write(), because
  // Chrome will silently reject on Android if the user denied the prompt for
  // this origin and the OS dispatcher will then claim the tap (showing
  // "Complete action with Tags / WeChat").
  const state = await getNfcPermissionState();
  if (state === "denied") {
    throw new Error(
      "NFC permission is blocked for this site. Open Chrome → tap the lock icon next to the URL → Permissions → NFC devices → Allow, then try again."
    );
  }

  // eslint-disable-next-line no-undef
  const ndef = new NDEFReader();
  try {
    await ndef.write(
      { records: [{ recordType: "url", data: url }] },
      { signal: opts.signal },
    );
  } catch (err) {
    const name = err && err.name;
    if (name === "NotAllowedError") {
      throw new Error(
        "NFC permission was denied. When prompted, tap Allow. If you don't see a prompt, open Chrome's site settings (lock icon → Permissions → NFC devices) and set it to Allow."
      );
    }
    if (name === "NotSupportedError") {
      throw new Error(
        "This device or browser does not support writing NFC tags. Use a recent Android phone in Chrome or Edge with NFC turned on."
      );
    }
    if (name === "NetworkError") {
      throw new Error(
        "The NFC tag could not be written. Make sure NFC is on, the tag is blank/writable (NTAG21x recommended), and held flat against the back of the phone."
      );
    }
    if (name === "AbortError") {
      throw new Error("NFC writing was cancelled.");
    }
    if (name === "InvalidStateError") {
      throw new Error(
        "The browser tab lost NFC focus. Keep this tab in the foreground (don't switch apps) while tapping the tag."
      );
    }
    throw new Error(
      err && err.message
        ? `NFC write failed: ${err.message}`
        : "NFC write failed. Please try again."
    );
  }
}
